import { useEffect, useState } from 'react'
import BilingAddress from './BillingAddress'
import SubscriptionDetails from './SubscriptionDetails'
import CreditCardDetails from './CreditCardDetails'
import Invoice from './Invoice'
import AddCreditCard from './AddCreditCard'
import UpdateBillingAddress from './UpdateBillingAddress'
import UpgradeSubscription from './UpgradeSubscription'
import UpgradePlan from './UpgradePlan'
import ACHDetails from './ACHDetails'
import Cookies from 'js-cookie'
import AddACH from './AddACH'
import useAuth from '../../hook/useAuth'
import { post_RenewSubscription, post_UpgradeSubscription } from '../../services/checkout'
import useConfirm from '../../hook/useConfirm'
import useTenant from '../../hook/useTenant'
import useGroup from '../../hook/useGroup'
import Wallet from './Wallet'
import { isPremiumPlan } from '../../utils/isPremiumPlan'
import { post_RechargeWallet } from '../../services/wallet'

export const INVOICE_TYPES = {
    Subscription: "Subscription",
    WalletRecharge: "WalletRecharge",
} as const;

export type InvoiceType = typeof INVOICE_TYPES[keyof typeof INVOICE_TYPES];

const Billing = () => {
    const plan = Cookies.get("plan-my-cdir") || "";
    const expires = Cookies.get("expires-my-cdir") || "";

    const { auth, setAuth }: any = useAuth();
    const { selectedTenantId, isSuperAdmin }: any = useGroup();

    const tenantId = isSuperAdmin
        ? selectedTenantId
        : Cookies.get("tenantId-my-cdir");
    const { setConfirmPopup, setData: setConfirmData }: any = useConfirm();
    const { handleTenantError, setRefreshTheSubscription }: any = useTenant();
    const [showPopupAddNewCreditCard, setShowPopupAddNewCreditCard] = useState<boolean>(false);
    const [showPopupUpdateBillingAddress, setShowPopupUpdateBillingAddress] = useState<boolean>(false);
    const [showPopupUpgradeSubscription, setShowPopupUpgradeSubscription] = useState<boolean>(false);
    const [billingAddressData, setBillingAddressData] = useState<any>({
        name: "",
        emailAddress: "",
        phoneNumber: "",
        street: "",
        street2: "",
        zip: "",
        state: "",
        city: "",
        country: ""
    });
    const [isRefreshCreditCardDetails, setIsRefreshCreditCardDetails] = useState<boolean>(true);
    const [planDetails, setPlanDetails] = useState<any | null>(null)
    const [invoiceType, setInvoiceType] = useState<InvoiceType>(INVOICE_TYPES.Subscription);
    const [showPopupForPayment, setShowPopupForPayment] = useState<boolean>(false);
    const [upgradPlan, setUpgradePlan] = useState<any | null>(null);
    const [isRefreshSubscriptionDetails, setIsRefreshSubscriptionDetails] = useState<boolean>(true);
    const [isRefreshWalletDetails, setIsRefreshWalletDetails] = useState<boolean>(true);
    const [isRefreshACHDetails, setIsRefreshACHDetails] = useState<boolean>(true);
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [showPopupAddNewACH, setShowPopupAddNewACH] = useState<boolean>(false);
    const [paymentType, setPaymentType] = useState<number>(0);
    const [activePaymentDetails, setActivePaymentDetails] = useState<any | null>(null);
    const [isUpgradingTheSubcription, setIsUpgradingTheSubcription] = useState<boolean>(false);
    const [isRefreshInvoice, setIsRefreshInvoice] = useState<boolean>(true);
    const [rechargeAmount, setRechargeAmount] = useState<string>("");

    const callTheAPIForUpgradeSubscription = (upgradedPlan: any, paymentDetails: any, callback: any) => {
        const fullName = auth?.givenname?.trim() || "";
        const [firstName, ...rest] = fullName?.split(/\s+/);
        const lastName = rest.length > 0 ? rest?.join(" ") : "";

        if (isUpgradingTheSubcription) {
            post_UpgradeSubscription({
                tenantId: tenantId,
                planId: upgradedPlan?.id,
                paymentType: paymentType === 0 ? "Credit Card" : "Bank ACH",
                paymentId: paymentDetails?.id > 0 ? paymentDetails?.id : 0,
                creditCardPayment: paymentType === 0
                    ? {
                        creditCardNumber: paymentDetails?.creditCardNumber,
                        cardHolderName: paymentDetails?.cardholderName,
                        ccvCode: paymentDetails?.ccvCode,
                        expirationDate: paymentDetails?.expirationDate
                    }
                    : null,
                achPayment: paymentType === 1
                    ? {
                        bankName: paymentDetails?.bankName,
                        accountNumber: paymentDetails?.accountNumber,
                        accountType: paymentDetails?.accountType,
                        routingNumber: paymentDetails?.routingNumber,
                        bankPhone: paymentDetails?.bankPhone,
                        accountHolderName: paymentDetails?.accountHolderName,
                        useAccountInfoInFuture: paymentDetails?.useAccountInfoInFuture,
                        signedAcknowledgement: paymentDetails?.signedAcknowledgement
                    }
                    : null,
                email: billingAddressData?.emailAddress,
                userName: auth?.userName,
                firstName: firstName,
                lastName: lastName
            })
                .then(() => {
                    setIsRefreshSubscriptionDetails(true);
                    setIsRefreshInvoice(true);
                    setIsRefreshCreditCardDetails(true);
                    setIsRefreshACHDetails(true);

                    setIsProcessing(false);
                    setIsUpgradingTheSubcription(false);
                    setPaymentType(0);
                    handleTenantError(null);
                    setRefreshTheSubscription(true);
                    setInvoiceType(INVOICE_TYPES.Subscription);

                    Cookies.set("plan-my-cdir", upgradedPlan?.planName, {
                        expires: new Date(expires)
                    });

                    setAuth((prev: any) => ({
                        ...prev,
                        planName: upgradPlan?.planName
                    }));

                    callback();
                })
                .catch(error => {
                    handleTenantError(error);
                    const errorMessage = error?.response?.data || error?.message || "File upload failed.";
                    setConfirmData({
                        title: 'Warning',
                        summary: errorMessage,
                        confirmBtn: 'Ok',
                        isConfirm: (callback: any) => {
                            callback();
                        }
                    });
                    setConfirmPopup(true);
                    setIsProcessing(false);
                })
        } else {
            post_RenewSubscription({
                tenantId: tenantId,
                paymentType: paymentType === 0 ? "Credit Card" : "Bank ACH",
                paymentId: paymentDetails?.id > 0 ? paymentDetails?.id : 0,
                creditCardPayment: paymentType === 0
                    ? {
                        creditCardNumber: paymentDetails?.creditCardNumber,
                        cardHolderName: paymentDetails?.cardholderName,
                        ccvCode: paymentDetails?.ccvCode,
                        expirationDate: paymentDetails?.expirationDate
                    }
                    : null,
                achPayment: paymentType === 1
                    ? {
                        bankName: paymentDetails?.bankName,
                        accountNumber: paymentDetails?.accountNumber,
                        accountType: paymentDetails?.accountType,
                        routingNumber: paymentDetails?.routingNumber,
                        bankPhone: paymentDetails?.bankPhone,
                        accountHolderName: paymentDetails?.accountHolderName,
                        useAccountInfoInFuture: paymentDetails?.useAccountInfoInFuture,
                        signedAcknowledgement: paymentDetails?.signedAcknowledgement
                    }
                    : null,
                userName: auth?.userName,
                firstName: firstName,
                lastName: lastName
            })
                .then(() => {
                    setIsRefreshSubscriptionDetails(true);
                    setIsRefreshInvoice(true);
                    setIsRefreshCreditCardDetails(true);
                    setIsRefreshACHDetails(true);

                    setIsProcessing(false);
                    setIsUpgradingTheSubcription(false);
                    setPaymentType(0);
                    handleTenantError(null);
                    setRefreshTheSubscription(true);
                    setInvoiceType(INVOICE_TYPES.Subscription);

                    callback();
                })
                .catch(error => {
                    handleTenantError(error);
                    const errorMessage = error?.response?.data || error?.message || "File upload failed.";
                    setConfirmData({
                        title: 'Warning',
                        summary: errorMessage,
                        confirmBtn: 'Ok',
                        isConfirm: (callback: any) => {
                            callback();
                        }
                    });
                    setConfirmPopup(true);
                    setIsProcessing(false);
                })
        }
    }

    const callTheAPIToRechargeTheWallet = (amount: number, paymentDetails: any, callback: any) => {
        const fullName = auth?.givenname?.trim() || "";
        const [firstName, ...rest] = fullName?.split(/\s+/);
        const lastName = rest.length > 0 ? rest?.join(" ") : "";

        post_RechargeWallet({
            tenantId: tenantId,
            amount: amount,
            currency: "USD",
            paymentType: paymentType === 0 ? "Credit Card" : "Bank ACH",
            paymentId: paymentDetails?.id > 0 ? paymentDetails?.id : 0,
            creditCardPayment: paymentType === 0
                ? {
                    creditCardNumber: paymentDetails?.creditCardNumber,
                    cardHolderName: paymentDetails?.cardholderName,
                    ccvCode: paymentDetails?.ccvCode,
                    expirationDate: paymentDetails?.expirationDate
                }
                : null,
            achPayment: paymentType === 1
                ? {
                    bankName: paymentDetails?.bankName,
                    accountNumber: paymentDetails?.accountNumber,
                    accountType: paymentDetails?.accountType,
                    routingNumber: paymentDetails?.routingNumber,
                    bankPhone: paymentDetails?.bankPhone,
                    accountHolderName: paymentDetails?.accountHolderName,
                    useAccountInfoInFuture: paymentDetails?.useAccountInfoInFuture,
                    signedAcknowledgement: paymentDetails?.signedAcknowledgement
                }
                : null,
            firstName: firstName,
            lastName: lastName,
            email: billingAddressData?.emailAddress
        })
            .then(() => {        
                setIsRefreshWalletDetails(true);
                setIsRefreshInvoice(true);
                setIsRefreshCreditCardDetails(true);
                setIsRefreshACHDetails(true);

                setIsProcessing(false);
                setIsUpgradingTheSubcription(false);
                setPaymentType(0);
                handleTenantError(null);
                setRefreshTheSubscription(true);
                setRechargeAmount("");
                setInvoiceType(INVOICE_TYPES.Subscription);

                callback();
            })
            .catch(error => {
                handleTenantError(error);
                const errorMessage = error?.response?.data || error?.message || "File upload failed.";
                setConfirmData({
                    title: 'Warning',
                    summary: errorMessage,
                    confirmBtn: 'Ok',
                    isConfirm: (callback: any) => {
                        callback();
                    }
                });
                setConfirmPopup(true);
                setIsProcessing(false);
            })
    }

    useEffect(() => {
        if (!tenantId) return;

        setIsRefreshSubscriptionDetails(true);
        setIsRefreshWalletDetails(true);
        setIsRefreshCreditCardDetails(true);
        setIsRefreshInvoice(true);
        setIsRefreshACHDetails(true);
    }, [tenantId]);

    return (
        <div className="min-h-[calc(100vh-60px)] py-10 px-4">

            <div className="w-full flex flex-col xl:flex-row gap-x-8 gap-y-6">
                <div className="w-full xl:w-[40%] flex flex-col gap-y-6">
                    <BilingAddress
                        setShowPopupUpdateBillingAddress={setShowPopupUpdateBillingAddress}
                        billingAddressData={billingAddressData}
                        setBillingAddressData={setBillingAddressData}
                    />

                    <CreditCardDetails
                        isRefresh={isRefreshCreditCardDetails}
                        setIsRefresh={setIsRefreshCreditCardDetails}
                        setShowPopupAddNewCreditCard={setShowPopupAddNewCreditCard}
                    />

                </div>

                <div className="flex-1 flex flex-col gap-y-6">
                    <SubscriptionDetails
                        setShowPopupUpgradeSubscription={setShowPopupUpgradeSubscription}
                        planDetails={planDetails}
                        setPlanDetails={setPlanDetails}
                        paymentType={paymentType}
                        setPaymentType={setPaymentType}
                        isProcessing={isProcessing}
                        setIsProcessing={setIsProcessing}
                        setShowPopupForPayment={setShowPopupForPayment}
                        setInvoiceType={setInvoiceType}
                        isRefreshSubscriptionDetails={isRefreshSubscriptionDetails}
                        setIsRefreshSubscriptionDetails={setIsRefreshSubscriptionDetails}
                        setActivePaymentDetails={setActivePaymentDetails}
                        isUpgradingTheSubcription={isUpgradingTheSubcription}
                        setIsUpgradingTheSubcription={setIsUpgradingTheSubcription}
                        callTheAPIForUpgradeSubscription={callTheAPIForUpgradeSubscription}
                    />

                    {
                        (isSuperAdmin || isPremiumPlan(plan)) && (
                            <Wallet
                                rechargeAmount={rechargeAmount}
                                setRechargeAmount={setRechargeAmount}
                                isRefreshWalletDetails={isRefreshWalletDetails}
                                setIsRefreshWalletDetails={setIsRefreshWalletDetails}
                                paymentType={paymentType}
                                setPaymentType={setPaymentType}
                                isProcessing={isProcessing}
                                setIsProcessing={setIsProcessing}
                                setShowPopupForPayment={setShowPopupForPayment}
                                setInvoiceType={setInvoiceType}
                                setActivePaymentDetails={setActivePaymentDetails}
                                callTheAPIToRechargeTheWallet={callTheAPIToRechargeTheWallet}
                            />
                        )
                    }

                    <Invoice
                        isRefresh={isRefreshInvoice}
                        setIsRefresh={setIsRefreshInvoice}
                    />

                    <ACHDetails
                        isRefresh={isRefreshACHDetails}
                        setIsRefresh={setIsRefreshACHDetails}
                        setShowPopupAddNewACH={setShowPopupAddNewACH}
                    />

                </div>

            </div>

            <UpdateBillingAddress
                showPopupUpdateBillingAddress={showPopupUpdateBillingAddress}
                setShowPopupUpdateBillingAddress={setShowPopupUpdateBillingAddress}
                billingAddressData={billingAddressData}
                setBillingAddressData={setBillingAddressData}
            />

            <AddCreditCard
                showPopupAddNewCreditCard={showPopupAddNewCreditCard}
                setShowPopupAddNewCreditCard={setShowPopupAddNewCreditCard}
                setIsRefresh={setIsRefreshCreditCardDetails}
            />

            <AddACH
                showPopupAddNewACH={showPopupAddNewACH}
                setShowPopupAddNewACH={setShowPopupAddNewACH}
                setIsRefresh={setIsRefreshACHDetails}
            />

            <UpgradeSubscription
                showPopupUpgradeSubscription={showPopupUpgradeSubscription}
                setShowPopupUpgradeSubscription={setShowPopupUpgradeSubscription}
                planDetails={planDetails}
                setShowPopupForPayment={setShowPopupForPayment}
                setUpgradePlan={setUpgradePlan}
                activePaymentDetails={activePaymentDetails}
                isProcessing={isProcessing}
                setIsProcessing={setIsProcessing}
                callTheAPIForUpgradeSubscription={callTheAPIForUpgradeSubscription}
            />

            <UpgradePlan
                showPopupForPayment={showPopupForPayment}
                setShowPopupForPayment={setShowPopupForPayment}
                invoiceType={invoiceType}
                upgradPlan={upgradPlan}
                rechargeAmount={rechargeAmount}
                isProcessing={isProcessing}
                setIsProcessing={setIsProcessing}
                callTheAPIForUpgradeSubscription={callTheAPIForUpgradeSubscription}
                callTheAPIToRechargeTheWallet={callTheAPIToRechargeTheWallet}
                paymentType={paymentType}
                setIsRefresh={setIsRefreshCreditCardDetails}
            />
        </div>
    )
}

export default Billing
